/**
 * The component kit. Small, token-driven, no styles of its own: every element
 * carries a class from theme/components.css.
 *
 * Overlays (Popover, Dialog) render through a portal at <body>, positioned
 * from the anchor's rectangle and flipped when the viewport runs out, so a
 * picker in a scrolling rail or at the far right of a toolbar never clips.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconArrowUp, IconCheck, IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react'
import { useOverlay } from './overlays'

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ')

/* ---------- buttons ---------- */

export function Button({ children, onClick, kind, small, on, disabled, title, type = 'button', ariaLabel, busy, className }: {
  children: ReactNode; onClick?: () => void; kind?: 'primary' | 'accent' | 'danger'; small?: boolean; on?: boolean; disabled?: boolean; title?: string; type?: 'button' | 'submit'; ariaLabel?: string; busy?: boolean; className?: string
}) {
  const cls = cx('btn', kind === 'primary' && 'is-primary', kind === 'accent' && 'is-accent', kind === 'danger' && 'is-danger', small && 'is-small', on && 'is-on', busy && 'is-busy', className)
  return <button type={type} className={cls} onClick={onClick} disabled={disabled || busy} title={title} aria-pressed={on} aria-label={ariaLabel} aria-busy={busy}>{busy ? <span className="spin" aria-hidden="true" /> : null}{children}</button>
}

export function IconButton({ children, onClick, label, expanded, badge, disabled, small, className }: { children: ReactNode; onClick?: () => void; label: string; expanded?: boolean; badge?: boolean; disabled?: boolean; small?: boolean; className?: string }) {
  return <button type="button" className={cx('ibtn', small && 'is-small', className)} onClick={onClick} aria-label={label} title={label} aria-expanded={expanded} disabled={disabled}>{children}{badge ? <i className="dot" /> : null}</button>
}

export function Segmented<T extends string>({ value, options, onChange, label, muted, wide, small }: {
  value: T; options: Array<{ value: T; label: string; title?: string }>; onChange: (value: T) => void; label: string; muted?: boolean; wide?: boolean; small?: boolean
}) {
  return (
    <div className={cx('seg', muted && 'is-muted', wide && 'is-wide', small && 'is-small')} role="group" aria-label={label}>
      {options.map(option => <button key={option.value} type="button" className={option.value === value ? 'is-on' : ''} aria-pressed={option.value === value} title={option.title} onClick={() => onChange(option.value)}>{option.label}</button>)}
    </div>
  )
}

export function Verdict({ band, children }: { band: 'good' | 'avg' | 'poor' | 'kill' | 'na'; children: ReactNode }) {
  return <span className={`verdict ${band}`}>{children}</span>
}

export function Pill({ children, muted, tone }: { children: ReactNode; muted?: boolean; tone?: 'good' | 'warn' | 'bad' }) {
  return <span className={cx('pill', muted && 'is-muted', tone && `is-${tone}`)}>{children}</span>
}

/* ---------- overlays ---------- */

type Align = 'start' | 'end'
interface Placement { top: number; left: number; width?: number; maxHeight: number; flipped: boolean }

function place(anchor: DOMRect, pop: { width: number; height: number }, align: Align, matchWidth: boolean, gap = 6): Placement {
  const vw = window.innerWidth, vh = window.innerHeight, margin = 8
  const width = matchWidth ? anchor.width : pop.width
  let left = align === 'end' ? anchor.right - width : anchor.left
  left = Math.max(margin, Math.min(left, vw - width - margin))
  const below = vh - anchor.bottom - gap - margin
  const above = anchor.top - gap - margin
  const flipped = pop.height > below && above > below
  const maxHeight = Math.max(120, flipped ? above : below)
  const top = flipped ? Math.max(margin, anchor.top - gap - Math.min(pop.height, maxHeight)) : anchor.bottom + gap
  return { top, left, width: matchWidth ? width : undefined, maxHeight, flipped }
}

/** Anchored popover, rendered in a portal, closed by outside click or Escape. */
export function Popover({ open, onClose, anchor, children, align = 'end', matchWidth, width, className, inline }: {
  open: boolean; onClose: () => void; anchor: ReactNode; children: ReactNode; align?: Align; matchWidth?: boolean; width?: number; className?: string
  /** Render in flow instead of a portal (rare: inside another fixed layer). */
  inline?: boolean
}) {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<Placement | null>(null)

  useOverlay(open, onClose)
  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || popRef.current?.contains(target)) return
      onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('mousedown', onDown) }
  }, [open, onClose])

  useLayoutEffect(() => {
    if (!open || inline) { setPos(null); return }
    const update = () => {
      const a = anchorRef.current?.getBoundingClientRect()
      const p = popRef.current
      if (!a || !p) return
      setPos(place(a, { width: width ?? p.offsetWidth, height: p.scrollHeight }, align, Boolean(matchWidth)))
    }
    update()
    const ro = new ResizeObserver(update)
    if (popRef.current) ro.observe(popRef.current)
    window.addEventListener('resize', update)
    document.addEventListener('scroll', update, true)
    return () => { ro.disconnect(); window.removeEventListener('resize', update); document.removeEventListener('scroll', update, true) }
  }, [open, inline, align, matchWidth, width])

  const style: CSSProperties | undefined = pos ? { '--pop-top': `${pos.top}px`, '--pop-left': `${pos.left}px`, '--pop-max-h': `${pos.maxHeight}px`, ...(pos.width ? { width: `${pos.width}px` } : width ? { width: `${width}px` } : {}) } as CSSProperties : (width ? { width: `${width}px` } as CSSProperties : undefined)
  const panel = open ? (
    <div ref={popRef} className={cx('popover', !inline && 'is-portal', inline && align === 'start' && 'is-left', pos?.flipped && 'is-up', pos === null && !inline && 'is-measuring', className)} role="dialog" style={style}>{children}</div>
  ) : null
  return (
    <span className={cx('pop-anchor', inline && 'is-inline')} ref={anchorRef}>
      {anchor}
      {inline ? panel : panel ? createPortal(panel, document.body) : null}
    </span>
  )
}

/** Modal dialog in a portal: backdrop, Escape closes, title and optional footer. */
export function Dialog({ open, onClose, title, children, actions, size = 'md', className, subtitle }: {
  open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; actions?: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'; className?: string; subtitle?: ReactNode
}) {
  const id = useId()
  useOverlay(open, onClose)
  if (!open) return null
  return createPortal(
    <div className="dlg-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
      <section className={cx('dlg', `is-${size}`, className)} role="dialog" aria-modal="true" aria-labelledby={id}>
        <header className="dlg-head">
          <div><h2 id={id} className="dlg-title">{title}</h2>{subtitle ? <div className="dlg-sub">{subtitle}</div> : null}</div>
          <IconButton label="Close" onClick={onClose}><IconX className="ic" /></IconButton>
        </header>
        <div className="dlg-body">{children}</div>
        {actions ? <footer className="dlg-foot">{actions}</footer> : null}
      </section>
    </div>,
    document.body,
  )
}

/* ---------- pickers ---------- */

export interface Option { value: string; label: string; count?: number | string; note?: string; disabled?: boolean }

/** The compact label / value / chevron row (rail) or tool field (toolbar)
 * that opens a picker. Single or multi; multi can cap and order the selection. */
export function PickerRow({ label, options, value, onChange, multi, clearable = true, searchable, defaultValue, scope, variant = 'rail', max, orderable, emptyLabel = 'Any', placeholder, note, footer, summaryOverride, busy, title }: {
  label: string
  options: Option[]
  value: string[] | string | null
  onChange: (next: string[] | string | null) => void
  multi?: boolean
  clearable?: boolean
  searchable?: boolean
  defaultValue?: string | null
  scope?: boolean
  variant?: 'rail' | 'tool'
  /** Multi: at most this many selected; further options are disabled. */
  max?: number
  /** Multi: show "move first" on the second selected option (order matters). */
  orderable?: boolean
  emptyLabel?: string
  placeholder?: string
  /** Secondary line under the row (rail only). */
  note?: ReactNode
  /** Extra content at the end of the popover (links, errors). */
  footer?: ReactNode
  summaryOverride?: string
  busy?: boolean
  title?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const selected = multi ? (value as string[]) : (value as string | null)
  const selectedList = multi ? (selected as string[]) : []
  const summary = summaryOverride ?? (multi
    ? (selectedList.length === 0 ? emptyLabel : selectedList.length === 1 ? (options.find(o => o.value === selectedList[0])?.label ?? '1 selected') : selectedList.length <= 2 ? selectedList.map(v => options.find(o => o.value === v)?.label ?? '?').join(', ') : `${selectedList.length} selected`)
    : (options.find(o => o.value === selected)?.label ?? emptyLabel))
  const isSet = multi ? selectedList.length > 0 : (selected !== null && selected !== defaultValue)
  const needle = search.trim().toLowerCase()
  const visible = needle ? options.filter(o => o.label.toLowerCase().includes(needle) || (o.note ?? '').toLowerCase().includes(needle)) : options
  const toggle = (optionValue: string) => {
    if (multi) {
      const current = selectedList
      if (current.includes(optionValue)) onChange(current.filter(v => v !== optionValue))
      else if (max === undefined || current.length < max) onChange([...current, optionValue])
    } else {
      onChange(optionValue)
      setOpen(false)
    }
  }
  const anchor = variant === 'tool' ? (
    <button type="button" className={cx('tool-field', 'is-picker', isSet && 'is-set', busy && 'is-busy')} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} title={title}>
      <span>{label}</span><b>{summary}</b><IconChevronDown className="ic" aria-hidden="true" />
    </button>
  ) : (
    <button type="button" className={cx('frow', isSet && 'is-set', scope && 'is-scope', busy && 'is-busy')} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} title={title}>
      <span className="fl">{label}</span>
      <span className="fv"><span>{summary}</span><IconChevronRight className="ic" aria-hidden="true" /></span>
    </button>
  )
  return (
    <div className={cx('picker', variant === 'tool' && 'is-tool')}>
      <Popover open={open} onClose={() => setOpen(false)} align={variant === 'tool' ? 'start' : 'start'} matchWidth={variant === 'rail'} width={variant === 'tool' ? 280 : undefined} anchor={anchor}>
        {searchable ? <input className="pop-search" placeholder={placeholder ?? 'Search'} value={search} onChange={e => setSearch(e.target.value)} autoFocus aria-label={`Search ${label}`} /> : null}
        <div className="pop-list" role="listbox" aria-multiselectable={multi || undefined} aria-label={label}>
          {clearable ? <button type="button" role="option" aria-selected={multi ? selectedList.length === 0 : selected === null} className={(multi ? selectedList.length === 0 : selected === null) ? 'is-on' : ''} onClick={() => { onChange(multi ? [] : null); if (!multi) setOpen(false) }}>{emptyLabel}</button> : null}
          {visible.map(option => {
            const on = multi ? selectedList.includes(option.value) : selected === option.value
            const capped = multi && !on && max !== undefined && selectedList.length >= max
            const position = multi ? selectedList.indexOf(option.value) : -1
            return <button type="button" role="option" aria-selected={on} key={option.value} className={cx(on && 'is-on', multi && 'is-check')} disabled={option.disabled || capped} onClick={() => toggle(option.value)}>
              {multi ? <span className={cx('chk-box', on && 'is-on')} aria-hidden="true">{on ? <IconCheck className="ic-sm" /> : null}</span> : null}
              <span className="opt-main"><span className="opt-label">{option.label}</span>{option.note ? <small>{option.note}</small> : null}</span>
              <span className="count">
                {option.count !== undefined ? option.count : null}
                {orderable && position === 1 ? <span className="mini-act" role="button" tabIndex={0} aria-label={`Move ${option.label} first`} onClick={event => { event.stopPropagation(); onChange([option.value, ...selectedList.filter(v => v !== option.value)]) }} onKeyDown={event => { if (event.key === 'Enter') { event.stopPropagation(); onChange([option.value, ...selectedList.filter(v => v !== option.value)]) } }}><IconArrowUp className="ic-sm" /></span> : null}
                {!multi && on ? <IconCheck className="ic-sm" aria-hidden="true" /> : null}
                {multi && orderable && position >= 0 ? <i className="ord">{position + 1}</i> : null}
              </span>
            </button>
          })}
          {visible.length === 0 ? <span className="t-faint pop-empty">No matches</span> : null}
        </div>
        {footer ? <div className="pop-foot">{footer}</div> : null}
      </Popover>
      {note ? <div className="frow-note">{note}</div> : null}
    </div>
  )
}

export function RangeRow({ label, min, max, onChange, unit, format }: { label: string; min: number | null; max: number | null; onChange: (min: number | null, max: number | null) => void; unit?: string; format?: (value: number) => string }) {
  const parse = (v: string) => (v.trim() === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null)
  const isSet = min !== null || max !== null
  const f = format ?? ((v: number) => String(v))
  const summary = !isSet ? 'Any' : max === null ? `${f(min!)}+` : min === null ? `≤ ${f(max)}` : `${f(min)} – ${f(max)}`
  return (
    <div className="picker">
      <div className={cx('frow', isSet && 'is-set')}>
        <span className="fl">{label}</span>
        <span className="fv"><span>{summary}{isSet && unit ? ` ${unit}` : ''}</span></span>
      </div>
      <div className="range-row">
        <input inputMode="numeric" placeholder="Min" value={min ?? ''} onChange={e => onChange(parse(e.target.value), max)} aria-label={`${label} minimum`} />
        <input inputMode="numeric" placeholder="Max" value={max ?? ''} onChange={e => onChange(min, parse(e.target.value))} aria-label={`${label} maximum`} />
        {isSet ? <button type="button" className="range-clear" onClick={() => onChange(null, null)} aria-label={`Clear ${label}`}><IconX className="ic-sm" /></button> : null}
      </div>
    </div>
  )
}

/** A plain rail row that opens something else (a dialog), with the same look as a picker. */
export function ActionRow({ label, value, isSet, onClick, note, expanded, title }: { label: string; value: string; isSet?: boolean; onClick: () => void; note?: ReactNode; expanded?: boolean; title?: string }) {
  return (
    <div className="picker">
      <button type="button" className={cx('frow', isSet && 'is-set')} onClick={onClick} aria-haspopup="dialog" aria-expanded={expanded} title={title}>
        <span className="fl">{label}</span>
        <span className="fv"><span>{value}</span><IconChevronRight className="ic" aria-hidden="true" /></span>
      </button>
      {note ? <div className="frow-note">{note}</div> : null}
    </div>
  )
}

export function Chevron() { return <IconChevronDown className="ic" aria-hidden="true" /> }

/* ---------- forms ---------- */

export function Field({ label, hint, error, children, inline, htmlFor, required }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; inline?: boolean; htmlFor?: string; required?: boolean }) {
  return (
    <div className={cx('fld', inline && 'is-inline', error ? 'has-error' : null)}>
      <label className="fld-label" htmlFor={htmlFor}>{label}{required ? <span className="req" aria-hidden="true"> *</span> : null}</label>
      <div className="fld-ctl">{children}</div>
      {hint && !error ? <div className="fld-hint">{hint}</div> : null}
      {error ? <div className="fld-error" role="alert">{error}</div> : null}
    </div>
  )
}

export function TextInput({ value, onChange, placeholder, id, disabled, maxLength, type = 'text', ariaLabel, autoFocus, prefix, suffix, mono, onKeyDown, required, invalid, name }: {
  value: string; onChange: (value: string) => void; placeholder?: string; id?: string; disabled?: boolean; maxLength?: number; type?: 'text' | 'email' | 'url' | 'search' | 'password'; ariaLabel?: string; autoFocus?: boolean; prefix?: ReactNode; suffix?: ReactNode; mono?: boolean; onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void; required?: boolean; invalid?: boolean; name?: string
}) {
  return (
    <span className={cx('inp', mono && 'is-mono', invalid && 'is-invalid', disabled && 'is-disabled')}>
      {prefix ? <span className="inp-affix">{prefix}</span> : null}
      <input id={id} name={name} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} maxLength={maxLength} aria-label={ariaLabel} autoFocus={autoFocus} onKeyDown={onKeyDown} required={required} aria-invalid={invalid || undefined} />
      {suffix ? <span className="inp-affix">{suffix}</span> : null}
    </span>
  )
}

/** Numeric text entry that keeps the raw string (exact money) and reports it unchanged. */
export function NumberInput({ value, onChange, placeholder, id, disabled, ariaLabel, prefix, suffix, step, min, max, invalid, decimals }: {
  value: string; onChange: (value: string) => void; placeholder?: string; id?: string; disabled?: boolean; ariaLabel?: string; prefix?: ReactNode; suffix?: ReactNode; step?: number | 'any'; min?: number; max?: number; invalid?: boolean; decimals?: boolean
}) {
  return (
    <span className={cx('inp', 'is-num', invalid && 'is-invalid', disabled && 'is-disabled')}>
      {prefix ? <span className="inp-affix">{prefix}</span> : null}
      <input id={id} type="text" inputMode={decimals === false ? 'numeric' : 'decimal'} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} aria-label={ariaLabel} step={step} min={min} max={max} aria-invalid={invalid || undefined} />
      {suffix ? <span className="inp-affix">{suffix}</span> : null}
    </span>
  )
}

export function Textarea({ value, onChange, placeholder, id, disabled, rows = 3, ariaLabel, maxLength, mono }: { value: string; onChange: (value: string) => void; placeholder?: string; id?: string; disabled?: boolean; rows?: number; ariaLabel?: string; maxLength?: number; mono?: boolean }) {
  return <textarea id={id} className={cx('txa', mono && 'is-mono')} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} rows={rows} aria-label={ariaLabel} maxLength={maxLength} />
}

export function Select({ value, onChange, options, id, disabled, ariaLabel, placeholder }: { value: string | null; onChange: (value: string | null) => void; options: Option[]; id?: string; disabled?: boolean; ariaLabel?: string; placeholder?: string }) {
  return (
    <span className={cx('sel', disabled && 'is-disabled')}>
      <select id={id} value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : e.target.value)} disabled={disabled} aria-label={ariaLabel}>
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
      </select>
      <IconChevronDown className="ic" aria-hidden="true" />
    </span>
  )
}

export function Checkbox({ checked, onChange, label, disabled, id, note }: { checked: boolean; onChange: (checked: boolean) => void; label: ReactNode; disabled?: boolean; id?: string; note?: ReactNode }) {
  return (
    <label className={cx('chk', disabled && 'is-disabled')}>
      <input id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
      <span className={cx('chk-box', checked && 'is-on')} aria-hidden="true">{checked ? <IconCheck className="ic-sm" /> : null}</span>
      <span className="chk-label">{label}{note ? <small>{note}</small> : null}</span>
    </label>
  )
}

export function Switch({ checked, onChange, label, disabled, id, note, ariaLabel, className }: { checked: boolean; onChange: (checked: boolean) => void; label: ReactNode; disabled?: boolean; id?: string; note?: ReactNode; ariaLabel?: string; className?: string }) {
  return (
    <label className={cx('sw', disabled && 'is-disabled', className)}>
      <input id={id} type="checkbox" role="switch" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} aria-checked={checked} aria-label={ariaLabel} />
      <span className={cx('sw-track', checked && 'is-on')} aria-hidden="true"><i /></span>
      <span className="sw-label">{label}{note ? <small>{note}</small> : null}</span>
    </label>
  )
}

/* ---------- states ---------- */

export function Skeletons({ count = 8, kind = 'card' }: { count?: number; kind?: 'card' | 'row' | 'block' }) {
  return <>{Array.from({ length: count }, (_, i) => <div key={i} className={cx('skeleton', kind !== 'card' && `is-${kind}`)} aria-hidden="true" />)}</>
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="empty"><b>{title}</b>{children}{action ? <div className="empty-act">{action}</div> : null}</div>
}

export function Alert({ title, children, info, warn, tight, action }: { title: ReactNode; children?: ReactNode; info?: boolean; warn?: boolean; tight?: boolean; action?: ReactNode }) {
  return <div className={cx('alert', info && 'is-info', warn && 'is-warn', tight && 'is-tight')} role={info ? 'status' : 'alert'}><b>{title}</b>{children}{action ? <div className="alert-act">{action}</div> : null}</div>
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <span className="spin" role="status" aria-label={label} />
}

/** Info glyph with a title tooltip; the compact-control design's "usual information icon". */
export function Info({ text }: { text: string }) {
  return <span className="info" title={text} aria-label={text} role="img">i</span>
}
